<script setup lang="ts">
import { OTranscript, useDateTime } from '@ai/ui-components'
import { watch, onUnmounted, computed } from 'vue'
import { useLiveTest } from '@/composables/livetest/useLiveTest'
import LivekitAudioPlayer from '@/components/BusinessLogic/LivekitAudioPlayer.vue'
import { useOTranscriptContext } from '@/composables/transcripts/useOTranscriptContext'
import AudioWaveform from '@/components/BusinessLogic/Widgets/AudioWaveform.vue'
import Disconnect from '@/assets/img/svg/icons/disconnect.svg'
import MicrophoneOff from '@/assets/img/svg/icons/microphone-off.svg'
import { RIGHT_PANEL_TOAST_VIEWPORT_PRESET, useToastViewportScope } from '@/composables/toast'
import CallDetailsMetadataCard from '@/components/BusinessLogic/CallDetails/CallDetailsMetadataCard.vue'
import { useCallDetailsRightPanelClass } from '@/components/BusinessLogic/CallDetails/useCallDetailsRightPanelClass'

defineOptions({ inheritAttrs: false })

const { t } = useI18n()
const { formatTimerDuration } = useDateTime()

const props = defineProps<{
  configId?: string
  configName?: string
}>()

const panelOpen = defineModel<boolean>('open', { default: false })

useToastViewportScope(panelOpen, RIGHT_PANEL_TOAST_VIEWPORT_PRESET)

const {
  isConnecting,
  isConnected,
  callEnded,
  error,
  durationSec,
  transcripts,
  tools,
  isMuted,
  localAudioTrack,
  connect,
  disconnect,
  endCall,
  toggleMute,
  audio,
} = useLiveTest()

const panelClass = useCallDetailsRightPanelClass(panelOpen)
const isCenteredStatus = computed(() => isConnecting.value || Boolean(error.value))

const transcriptLiveAudio = computed(() => ({
  mode: 'live' as const,
  durationSec: durationSec.value,
  isListening: isConnected.value && !callEnded.value,
  canListen: false,
}))
const transcriptContext = useOTranscriptContext({
  transcripts,
  events: tools,
  audio: transcriptLiveAudio,
})

// Get remote audio track for waveform (agent audio)
// Use audioUpdateTick to force reactivity when tracks change
const remoteAudioTrack = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  audio.audioUpdateTick.value // Force reactivity
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!audio.remoteTracks.value) return null
  const tracks = Array.from(audio.remoteTracks.value.values())
  return tracks[0] ?? null
})

const openPanel = async (configId?: string) => {
  panelOpen.value = true
  await connect({ configId: configId ?? props.configId })
}

const onEndTest = async () => {
  // Send RPC to agent to end call - agent will disconnect and trigger handleCallEnded
  await endCall()
}

const retry = async () => {
  await connect({ configId: props.configId })
}

// Auto-connect when panel opens, auto-disconnect when panel closes
watch(panelOpen, async (isOpen, wasOpen) => {
  if (isOpen && !wasOpen && !isConnected.value && !isConnecting.value) {
    // Panel just opened via v-model, start connection
    await connect({ configId: props.configId })
  } else if (!isOpen && (isConnected.value || callEnded.value)) {
    // Panel closed, disconnect and reset state
    await disconnect()
  }
})

onUnmounted(() => {
  void disconnect()
})

defineExpose({ openPanel })
</script>

<template>
  <ORightPanel
    v-model:open="panelOpen"
    :title="t('live_test')"
    floating
    :panel-class="panelClass"
    header-class="pb-l pl-6"
    content-class="-mb-6 min-h-0 overflow-hidden radius-b-xl pl-0 pr-0 w-[calc(100%+1.5rem)]"
  >
    <template #header>
      <LivekitAudioPlayer v-if="audio" :audio="audio" />

      <CallDetailsMetadataCard card-class="min-h-[146px]">
        <template #header>
          <!-- Status banner -->
          <div
            class="flex w-full flex-col items-center justify-center gap-xs text-neutral-1"
            :class="{ 'flex-1': isCenteredStatus }"
            data-testid="live-test-status-timer"
          >
            <!-- Connecting state -->
            <template v-if="isConnecting">
              <OSpinner size="M" />
              <span class="typography-body-1 mt-2">{{ t('connecting') }}...</span>
            </template>

            <!-- Error state -->
            <template v-else-if="error">
              <UiIcon name="warning" class="size-8 text-error" />
              <span class="typography-body-1 mt-2 text-error">
                {{
                  error === 'microphone_permission_denied'
                    ? t('microphone_permission_denied')
                    : t('connection_error')
                }}
              </span>
              <OButton
                class="mt-4"
                :label="t('retry')"
                variant="secondary"
                size="S"
                @click="retry"
              />
            </template>

            <!-- Connected state -->
            <template v-else-if="isConnected">
              <div class="flex items-center space-x-2">
                <span class="relative flex h-3 w-3">
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"
                  />
                  <span class="relative inline-flex rounded-full h-3 w-3 bg-success" />
                </span>
                <span class="typography-body-1">{{ t('live_call') }}</span>
              </div>
              <span class="typography-display-1">
                {{
                  formatTimerDuration({
                    value: durationSec,
                    alwaysIncludeHours: true,
                    fallback: '00:00:00',
                  })
                }}
              </span>
              <div class="flex space-x-2">
                <!-- Audio waveform -->

                <AudioWaveform
                  v-if="false"
                  :key="`${localAudioTrack?.mediaStreamTrack?.id}-${remoteAudioTrack?.mediaStreamTrack?.id}`"
                  :local-track="localAudioTrack"
                  :remote-track="remoteAudioTrack"
                  :local-muted="isMuted"
                />
              </div>
            </template>

            <!-- Call ended by agent state -->
            <template v-else-if="callEnded">
              <div class="flex items-center space-x-2">
                <span class="typography-body-1">{{ t('call_ended') }}</span>
              </div>
              <span class="typography-display-1">
                {{
                  formatTimerDuration({
                    value: durationSec,
                    alwaysIncludeHours: true,
                    fallback: '00:00:00',
                  })
                }}
              </span>
            </template>
          </div>
        </template>

        <template #footer>
          <!-- Action buttons -->
          <div
            v-if="isConnected || callEnded"
            class="grid w-full gap-3"
            :class="callEnded ? 'grid-cols-1' : 'grid-cols-[1fr_auto]'"
          >
            <OButton
              size="L"
              :label="t('disconnect')"
              variant="secondary"
              :disabled="callEnded"
              class="w-full"
              @click="onEndTest"
            >
              <template #icon><Disconnect class="size-4" /></template>
            </OButton>
            <OButton
              v-if="!callEnded"
              class="max-w-14"
              size="L"
              variant="secondary"
              @click="toggleMute"
            >
              <template #icon>
                <MicrophoneOff v-if="isMuted" class="size-4" />
                <UiIcon v-else name="microphone" class="size-4" />
              </template>
            </OButton>
          </div>
        </template>
      </CallDetailsMetadataCard>
    </template>

    <template #default>
      <!-- Error state content -->
      <div v-if="error" class="flex h-full min-h-0 items-start justify-center pt-16">
        <div class="max-w-60">
          <OState type="empty" variant="2" />
        </div>
      </div>
      <div
        v-else-if="isConnected || callEnded"
        class="flex h-full min-h-0 flex-col overflow-hidden"
      >
        <div class="min-h-0 flex-1 border-t border-neutral-5">
          <OTranscript
            class="h-full"
            :audio="transcriptContext.audio.value"
            :transcript="transcriptContext.transcript.value"
            :events="transcriptContext.events.value"
            :disabled-play-tooltip="false"
            :show-duration="false"
            :show-play-button="false"
            :show-soundbar="false"
          >
            <template #empty-transcript>
              <div class="max-w-60">
                <OState
                  type="empty"
                  variant="2"
                  :state="{
                    title: t('waiting_for_agent'),
                    description: t('speak_to_start_conversation'),
                  }"
                />
              </div>
            </template>

            <template #empty-events>
              <div class="max-w-60">
                <OState type="empty" variant="2" />
              </div>
            </template>
          </OTranscript>
        </div>
      </div>
    </template>
  </ORightPanel>

  <slot :open-panel="openPanel" />
</template>
